<template>
    <div>
        <div v-if="isLoading == true" class="loading">
            <p>Loading data, please wait...</p>
        </div>
        <div v-else class="item">
            <h6><template v-if="variableType != ''">{{ variableType }}: </template>{{ userPoint.name }}<br/><span class="latlon-title">Latitude: {{ userPoint.lat.toFixed(3) }}, Longitude: {{ userPoint.lon.toFixed(3) }}</span></h6>
            <table class="table">
                <tbody>
                    <tr v-for="item in viewItems" :key="item.key">
                        <td>
                            {{ item.monthName }}
                        </td>
                        <td>
                            <div class="color-item" :style="{ backgroundColor: item.color }"></div>
                        </td>
                        <td>
                            {{ item.label }}
                        </td>
                    </tr>
                </tbody>
            </table>
            <div class="util-buttons">
                <template v-if="!isSaved && allowSave">
                    <a class="btn btn-success" href="#" @click="save_Clicked">Save</a>&nbsp;
                    <a class="btn btn-danger" href="#" @click="discard_Clicked">Don't save</a>
                </template>                
                <a v-if="isSaved && allowDelete" class="btn btn-danger" href="#" @click="delete_Clicked">Delete</a>
            </div>
        </div>        
    </div>
</template>

<script>

import logger from '@/lib/logger.js'
import events from '@/lib/events.js'
import MonthIterator from '@/lib/MonthIterator.js'
import { ForecastVarType } from '@/lib/Forecast.js'
import { ServiceController } from '@/lib/ServiceController.js'
import DataUtils from '@/lib/DataUtils.js'

export default {
    data() {
        return {
            userPoint: null,
            selVariable: null,
            dataValues: null,
            forecast: null,
            viewItems: [],
            isLoading: true,
            allowDelete: true,
            allowSave: true,
            numMonths: 0
        }        
    },
    computed: {
        isSaved() {
            let result = false
            if (this.userPoint != null)
                result = this.userPoint.isSaved()
            return result
        },
        variableType() {
            if (this.selVariable != null) {
                switch(this.selVariable.varType) {
                    case ForecastVarType.VT_FORECAST:
                        return 'Forecast'
                    case ForecastVarType.VT_HISTORICAL:
                        return 'Historical'
                }
            }
            return ''
        }
    },
    props: [ 'onSave', 'onDiscard' ],
    methods: {
        resetData() {
            logger.log('UserPointDataView::resetData()')

            this.userPoint = null,
            this.selVariable = null,
            this.dataValues = null,
            this.varItems = [],
            this.isLoading = true
            this.forecast = null
            this.numMonths = 0
        },
        updateData(userPoint, forecast, selVariable, dataValues, numMonths, showFullHistorical) {
            logger.log('UserPointDataView::updateData()', { 
                userPoint: userPoint,
                selVariable: selVariable,
                dataValues: dataValues,
                numMonths: numMonths
            })

            this.userPoint = userPoint
            this.selVariable = selVariable
            this.dataValues = dataValues
            this.forecast = forecast
            this.numMonths = numMonths
            this.showFullHistorical = showFullHistorical

            this.createViewItems()

            this.isLoading = false
        },
        save_Clicked() {
            logger.log('UserPointDataView::save_Clicked()')

            events.$emit(events.ACTION_ADD_POINT, this.userPoint.lat, this.userPoint.lon, 'New point')

            if (typeof this.onSave == 'function')
                this.onSave(this.userPoint)
        },
        discard_Clicked() {
            logger.log('UserPointDataView::discard_Clicked()')            

            if (typeof this.onDiscard == 'function')
                this.onDiscard(this.userPoint)
        },
        delete_Clicked() {
            logger.log('UserPointDataView::delete_Clicked()')
            events.$emit(events.ACTION_PROMPT, 'Delete point?', 'Do you want to delete the selected point?', ['yes', 'no'], (btnID) => {
                if (btnID == 'yes') {
                    ServiceController.deleteUserPoint(this.userPoint.id)                        
                }
            })
        },
        createViewItems() {
            logger.log('UserPointDataView::createViewItems()')

            let monthIterator = null
            
            let startDataIndex = 0
            let dataCount = 0
            
            switch(this.selVariable.varType) {
                case ForecastVarType.VT_FORECAST:
                    logger.log('Creating monthIterator for forecast values, month=' + this.forecast.forecastID.getFirstForecastMonth())
                    monthIterator = new MonthIterator(this.forecast.forecastID.getFirstForecastMonth())
                    startDataIndex = 0
                    dataCount = this.dataValues.length
                    break;
                case ForecastVarType.VT_HISTORICAL:
                    logger.log('Creating monthIterator for historical values')
                    if (this.showFullHistorical) {
                        monthIterator = new MonthIterator(1)
                        startDataIndex = 0
                        dataCount = this.dataValues.length
                    }
                    else {
                        monthIterator = new MonthIterator(this.forecast.forecastID.getFirstForecastMonth())
                        startDataIndex = this.forecast.forecastID.getFirstForecastMonth() - 1
                        dataCount = startDataIndex + this.numMonths
                    }                    
                    break;
                default:
                    logger.log('Unable to create monthIterator, unsupported varType')
            }

            if (monthIterator == null)
                return []

            let viewItems = []

            for(let dataIndex = startDataIndex; dataIndex < dataCount; dataIndex++) {
                let offsetDataIndex = dataIndex
                if (offsetDataIndex >= this.dataValues.length)
                    offsetDataIndex = offsetDataIndex - this.dataValues.length

                let value = this.dataValues[offsetDataIndex]

                if (DataUtils.isNoData(value)) {                    
                    let $data = {
                        monthName: monthIterator.toString('$month'),
                        color: '#fff',
                        label: 'No data at this location'
                    }
                    monthIterator.nextMonth()
                    viewItems.push($data)
                    continue
                }

                let colorClass = this.selVariable.legend.getColorClass(value)
                
                if (colorClass == null) {                    
                    let $data = {
                        monthName: '',
                        color: '#fff',
                        label: 'N/A'
                    }
                    monthIterator.nextMonth()
                    viewItems.push($data)
                    continue
                }

                let result = {
                    monthName: monthIterator.toString('$month'),
                    color: colorClass.color,
                    label: colorClass.text
                }

                if (this.selVariable.units != '') {
                    result.label = result.label + ' (' + this.selVariable.units + ')'
                }

                monthIterator.nextMonth()
                viewItems.push(result)
            }

            logger.log('Setting view items', viewItems)

            this.viewItems = viewItems
        },
        updated() {
            this.$emit('update-called')
        }
    }
}
</script>

<style>
    .color-item {
        position: relative;
        display: inline-block;
        padding: 5px;
        border: 1px solid black;
    }

    .latlon-title {
        color: #aaa;        
        font-size: 10pt;
    }

    .util-buttons a {
        color: white !important;
    }
</style>
